import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SelfAssessmentInput, { checkSelfAssessment } from "../../components/inputs/SelfAssessmentInput";
import { selectExtendedVariant, extendedMaySource } from "../../generators/extendedVariantSelection";
import { ScoringNotes } from "./May2025Assessment";

const ALGEBRA = Array.from({ length: 11 }, (_, i) => ({ q: i + 2 })).filter(({ q }) => q !== 2);
const TRAPEZOID = [{ n: 2, d: 5 }, { n: 3, d: 5 }, { n: 2, d: 3 }, { n: 3, d: 4 }, { n: 4, d: 5 }];
const CONE = Array.from({ length: 12 }, (_, i) => ({ distance: i + 1 })).filter(({ distance }) => distance !== 5);
const DEFINITIONS = {
  "2": { type: "MaturaExtendedAlgebraInequalityProofTask", points: 3, pool: ALGEBRA, original: { q: 2 } },
  "7": { type: "MaturaExtendedTrapezoidProofTask", points: 4, pool: TRAPEZOID, original: { n: 1, d: 2 } },
  "12.1": { type: "MaturaExtendedConeVolumeProofTask", points: 2, pool: CONE, original: { distance: 5 } },
};

export function generateProof(taskNumber, options = {}) {
  const definition = DEFINITIONS[taskNumber];
  if (!definition) throw new Error("Unknown May 2025 proof");
  const original = options.variant === "original";
  return { type: definition.type, taskNumber, points: definition.points, assessmentMode: "self",
    ...(original ? definition.original : selectExtendedVariant(definition.pool, options)),
    source: extendedMaySource(taskNumber, original) };
}
export function getVariantCounts() {
  return Object.fromEntries(Object.entries(DEFINITIONS).map(([number, value]) => [number, value.pool.length]));
}
export function renderTask(task) {
  let content;
  if (task.taskNumber === "2") content = <>
    <p>Wykaż, że dla każdych dodatnich liczb rzeczywistych <InlineMath math="a"/> i <InlineMath math="b"/>, dla których <InlineMath math={`a\\ne${task.q}b`}/>, zachodzi nierówność:</p>
    <BlockMath math={`(a+${task.q}b)^3>${4 * task.q}a^2b+${4 * task.q ** 2}ab^2`} />
  </>;
  else if (task.taskNumber === "7") content = <>
    <p>W trapezie <InlineMath math="ABCD"/> o podstawach <InlineMath math="AB"/> i <InlineMath math="CD"/> punkty <InlineMath math="E"/> i <InlineMath math="F"/> są środkami ramion <InlineMath math="AD"/> i <InlineMath math="BC"/>.
      Stosunek pola trapezu <InlineMath math="EFCD"/> do pola trapezu <InlineMath math="ABFE"/> wynosi:</p>
    <BlockMath math={String.raw`\frac{P_{EFCD}}{P_{ABFE}}=\frac{${task.n}}{${task.d}}`} />
    <p>Wykaż, że:</p><BlockMath math={String.raw`\frac{|CD|}{|AB|}=\frac{${3 * task.n - task.d}}{${3 * task.d - task.n}}`} />
  </>;
  else content = <>
    <p>Rozważamy stożki o wysokości <InlineMath math="h"/> większej od <InlineMath math={String(task.distance)}/>. Odległość środka podstawy od tworzącej wynosi <InlineMath math={String(task.distance)}/>.</p>
    <p>Wykaż, że objętość stożka w zależności od wysokości jest dana wzorem:</p>
    <BlockMath math={String.raw`V(h)=\frac{\pi}{3}\cdot\frac{${task.distance ** 2}h^3}{h^2-${task.distance ** 2}}`} />
  </>;
  return <div><p><strong>Zadanie otwarte (0–{task.points} pkt). Samoocena.</strong></p>{content}<p><strong>Zapisz rozumowanie.</strong></p></div>;
}
export function renderExplanation(task) {
  if (task.taskNumber === "2") return <div>
    <p>Przenosimy prawą stronę na lewą i rozkładamy różnicę na czynniki:</p>
    <BlockMath math={`(a+${task.q}b)^3-${4 * task.q}a^2b-${4 * task.q ** 2}ab^2=(a-${task.q}b)^2(a+${task.q}b)`} />
    <p>Pierwszy czynnik jest dodatni, ponieważ <InlineMath math={`a\\ne${task.q}b`}/>. Drugi jest dodatni, ponieważ <InlineMath math="a"/> i <InlineMath math="b"/> są dodatnie. Ich iloczyn jest dodatni, co kończy dowód.</p>
  </div>;
  if (task.taskNumber === "7") return <div>
    <p>Oznaczamy długości podstaw przez <InlineMath math="a=|AB|"/> i <InlineMath math="b=|CD|"/>, a wysokość przez <InlineMath math="h"/>. Odcinek łączący środki ramion <InlineMath math="EF"/> ma długość <InlineMath math="\frac{a+b}{2}"/> i dzieli wysokość na połowy.</p>
    <BlockMath math={String.raw`\frac{P_{EFCD}}{P_{ABFE}}=\frac{\frac{a+b}{2}+b}{a+\frac{a+b}{2}}=\frac{a+3b}{3a+b}=\frac{${task.n}}{${task.d}}`} />
    <BlockMath math={String.raw`${task.d}(a+3b)=${task.n}(3a+b)\quad\Longrightarrow\quad (${3 * task.d - task.n})b=(${3 * task.n - task.d})a`} />
    <p>Dzielimy przez dodatnie <InlineMath math="a"/> i dodatni współczynnik przy <InlineMath math="b"/>, uzyskując wymagany stosunek. Dowód nie zależy od długości ramion ani wysokości.</p>
  </div>;
  return <div>
    <p>W przekroju osiowym połowa trójkąta ma przyprostokątne <InlineMath math="r"/> i <InlineMath math="h"/> oraz przeciwprostokątną <InlineMath math="l"/>. Jej wysokość opuszczona ze środka podstawy na tworzącą ma długość <InlineMath math={String(task.distance)}/>. Obliczamy pole na dwa sposoby:</p>
    <BlockMath math={String.raw`\frac12 rh=\frac12\cdot ${task.distance}\cdot l,\qquad l^2=r^2+h^2`} />
    <BlockMath math={String.raw`r^2h^2=${task.distance ** 2}(r^2+h^2)\quad\Longrightarrow\quad r^2=\frac{${task.distance ** 2}h^2}{h^2-${task.distance ** 2}}`} />
    <p>Mianownik jest dodatni, ponieważ <InlineMath math={`h>${task.distance}`}/>. Podstawiamy do wzoru na objętość:</p>
    <BlockMath math={String.raw`V=\frac13\pi r^2h=\frac{\pi}{3}\cdot\frac{${task.distance ** 2}h^3}{h^2-${task.distance ** 2}}`} />
  </div>;
}
export function getRubric(task) {
  if (task.taskNumber === "2") return [
    "Brak poprawnej metody. Sprawdzenie kilku przykładów nie jest dowodem.",
    "Poprawnie rozwijasz sześcian i dochodzisz do wyrażenia umożliwiającego rozkład na czynniki (lub równoważnego istotnego przekształcenia).",
    `Otrzymujesz (a−${task.q}b)²(a+${task.q}b)>0 lub równoważny warunek, ale nie uzasadniasz jeszcze w pełni dodatniości.`,
    `Poprawny pełny dowód: rozkład lub inna poprawna metoda oraz uzasadnienie ścisłej dodatniości z a,b>0 i a≠${task.q}b.`,
  ];
  if (task.taskNumber === "7") return [
    "Brak poprawnej metody lub brak rozumowania.",
    "Zapisujesz jeden istotny poprawny związek pól trapezów z długościami podstaw i wysokością.",
    "Łączysz dwa istotne związki albo zapisujesz iloraz pól wraz z danymi i długością odcinka środkowego.",
    "Po podstawieniu długości EF zapisujesz równanie wiążące obie podstawy z podanym stosunkiem pól.",
    "Przeprowadzasz pełne poprawne rozumowanie i otrzymujesz wymagany stosunek podstaw.",
  ];
  return ["Brak poprawnej metody lub brak rozwiązania.",
    "Zapisujesz poprawną zależność między promieniem, wysokością i odległością środka podstawy od tworzącej.",
    "Poprawnie wyprowadzasz zależność i podstawiasz ją do wzoru na objętość, uzyskując żądany wzór."];
}
export function renderInput(task, answer, onChange, disabled) {
  return <><SelfAssessmentInput task={task} answer={answer} onChange={onChange} disabled={disabled}
    renderTaskPreview={() => renderTask(task)}
    explanation={renderExplanation(task)} rubric={getRubric(task)} scoringNotes={<ScoringNotes task={task} />} /></>;
}
export const checkAnswer = checkSelfAssessment;
export function renderAnswer(task) { return renderExplanation(task); }
export function renderUserAnswer(answer = {}) {
  return <div><p style={{ whiteSpace: "pre-wrap" }}>{answer.work || "Brudnopis: tablica lub kartka."}</p>
    <p>{answer.reviewConfirmed ? `Samoocena: ${answer.selfScore} pkt` : "Samoocena niepotwierdzona."}</p></div>;
}
export function proofAdapter(number) {
  return { generateTask: (options) => generateProof(number, options), renderTask, renderInput,
    checkAnswer, renderAnswer, renderExplanation, renderUserAnswer, assessmentMode: "self" };
}
